/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import * as Yup from 'yup';
import { SCHEMA, VALIDATORS } from './validators';

type ValidationRule = {
  type: typeof VALIDATORS & {
    [key: string]: any;
  };
  message?: string;
  value?: number | string | any;
  [key: string]: any;
};

type Field = {
  name: string;
  type: typeof SCHEMA;
  rules: ValidationRule[];
  shape?: string | Field[];
};
/**
 * @typedef {Object} ValidationRule
 * @property {string} type - The type of validation (e.g., 'required', 'min', 'max', 'test').
 * @property {string} [message] - The validation message.
 * @property {number} [value] - The value for min/max validations.
 * @property {string} [name] - The name for custom test validations.
 * @property {Function} [test] - The custom test function.
 */

/**
 * @typedef {Object} Field
 * @property {string} name - The name of the field.
 * @property {string} type - The type of the field (e.g., 'string', 'number', 'email', 'object', 'array').
 * @property {ValidationRule[]} [validations] - The validations for the field.
 * @property {Field[]} [fields] - The nested fields for object type.
 */

/**
 * ValidateBuilder class provides a fluent interface for building validation schemas using Yup.
 *
 * @class
 * @name ValidateBuilder
 * @example
 */
class ValidateBuilder {
  fields: Field[] = [];
  validators: typeof VALIDATORS;
  constructor(extraValidators = {}) {
    /** @type {Field[]} */
    this.fields = [];
    this.validators = Object.assign(VALIDATORS, extraValidators);
  }

  createFieldRule(validator, rules) {
    let _validator = validator;
    rules.forEach((rule) => {
      const { type, ...options } = rule;
      if (this.validators[type]) {
        _validator = this.validators[type](validator, options);
      } else {
        throw new Error(`Invalid validation rule: ${type}`);
      }
    });

    return _validator;
  }

  /**
   * Adds a field to the form.
   *
   * @param {ValidationRule} field - The field to add.
   * @returns {ValidateBuilder} The current instance for chaining.
   */
  addField(name, type, rules = [], shape = '') {
    this.fields.push({ name, type, rules, shape });
    return this;
  }

  /**
   * Build the validation schema.
   *
   * @returns {Yup.Schema} The validation schema.
   */
  build() {
    const generateSchema = (fields) => {
      const shape = {};

      fields.forEach((field) => {
        let validator;
        switch (field.type) {
          case 'string':
            validator = Yup.string();
            break;
          case 'number':
            validator = Yup.number();
            break;
          case 'object':
            validator = Yup.object().shape(generateSchema(field.shape));
            break;
          case 'array':
            validator = Yup.array();
            break;
          case 'mixed':
            validator = Yup.mixed();
            break;
          case 'boolean':
            validator = Yup.boolean();
            break;
          case 'date':
            validator = Yup.date();
            break;
          // Add more types as needed
          default:
            break;
        }
        validator = this.createFieldRule(validator, field.rules);
        shape[field.name] = validator;
      });

      return shape;
    };

    return Yup.object().shape(generateSchema(this.fields));
  }
}

export default ValidateBuilder;
